<template>
  <div ref="root" class="relative inline-block text-left" @keydown.esc.stop="close(true)">
    <button ref="trigger" type="button" v-on:click="toggle" :disabled="disabled"
            :aria-label="label" :title="iconOnly ? label : null" aria-haspopup="menu" :aria-expanded="isOpen ? 'true' : 'false'"
            :aria-controls="menuId" @keydown.down.prevent="open"
            :class="triggerClass">
      <font-awesome-icon v-if="icon" :icon="icon" class="h-4 w-4" :class="{'mr-2': !iconOnly}" aria-hidden="true"/>
      <span v-if="!iconOnly">{{ label }}</span>
    </button>

    <div v-if="isOpen" :id="menuId" role="menu" :aria-label="label"
         class="absolute z-30 mt-1 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
         :class="[alignClass, menuWidthClass]"
         @keydown.down.prevent="focusItem(1)" @keydown.up.prevent="focusItem(-1)"
         @keydown.home.prevent="focusEdge(0)" @keydown.end.prevent="focusEdge(-1)" @keydown.tab="close(false)">
      <button v-for="item in items" :key="item.key" ref="items" type="button" role="menuitem" tabindex="-1"
              :disabled="item.disabled" v-on:click="choose(item)"
              class="flex w-full items-start gap-3 px-3 py-2 text-left text-sm disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:bg-gray-100 hover:bg-gray-100"
              :class="item.danger ? 'text-red-700' : 'text-gray-700'">
        <font-awesome-icon v-if="item.icon" :icon="item.icon" class="mt-0.5 h-4 w-4 flex-shrink-0"
                           :class="item.danger ? 'text-red-600' : 'text-gray-500'" aria-hidden="true"/>
        <span class="min-w-0">
          <span class="block font-medium">{{ item.label }}</span>
          <span v-if="item.description" class="block text-xs text-gray-600">{{ item.description }}</span>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
let menuCounter = 0

/*
 * Menú de acciones accesible (botón + lista `role="menu"`): flechas, Inicio/Fin,
 * Escape devuelve el foco al botón y un clic fuera lo cierra. Cada item es
 * { key, label, icon?, description?, danger?, disabled? } y al elegirlo se emite
 * `select` con su `key`.
 */
export default {
  name: "ActionMenuComponent",
  props: {
    items: {
      type: Array,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: 'ellipsis-h'
    },
    // Solo icono (con `aria-label` y `title`) o icono + texto visible.
    iconOnly: {
      type: Boolean,
      default: true
    },
    align: {
      type: String,
      default: 'right',
      validator: (value) => ['left', 'right', 'center'].includes(value)
    },
    wide: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    triggerClass: {
      type: [String, Array, Object],
      default: 'inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue'
    }
  },
  emits: ['select'],
  data() {
    menuCounter += 1
    return {
      isOpen: false,
      menuId: 'action-menu-' + menuCounter,
    }
  },
  computed: {
    alignClass() {
      return {left: 'left-0 origin-top-left', right: 'right-0 origin-top-right', center: 'left-1/2 -translate-x-1/2'}[this.align]
    },
    menuWidthClass() {
      return this.wide ? 'w-72' : 'w-56'
    }
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onOutsideClick)
  },
  methods: {
    toggle() {
      this.isOpen ? this.close(false) : this.open()
    },
    open() {
      if (this.disabled || this.isOpen) return
      this.isOpen = true
      document.addEventListener('mousedown', this.onOutsideClick)
      this.$nextTick(() => this.focusEdge(0))
    },
    close(returnFocus) {
      if (!this.isOpen) return
      this.isOpen = false
      document.removeEventListener('mousedown', this.onOutsideClick)
      if (returnFocus) this.$refs.trigger?.focus()
    },
    choose(item) {
      if (item.disabled) return
      this.close(true)
      this.$emit('select', item.key)
    },
    onOutsideClick(event) {
      if (!this.$refs.root?.contains(event.target)) this.close(false)
    },
    enabled() {
      return (this.$refs.items ?? []).filter((element) => !element.disabled)
    },
    focusEdge(edge) {
      const items = this.enabled()
      items[edge < 0 ? items.length - 1 : 0]?.focus()
    },
    focusItem(offset) {
      const items = this.enabled()
      if (!items.length) return
      const current = items.indexOf(document.activeElement)
      items[(current + offset + items.length) % items.length].focus()
    }
  }
}
</script>
